import { isNil } from 'lodash'
import { ComponentType, ReactNode, useEffect, useMemo, useState } from 'react'
import { BsPlayCircleFill } from 'react-icons/bs'
import { IconProps } from 'tamagui-phosphor-icons'
import { z } from 'zod'

import { StoreObject, useMutation, useQuery } from '@apollo/client'
import {
  DEFAULT_PRODUCT_FEATURES,
  DEFAULT_PRODUCT_HEADLINE,
} from '@bounty/constants'
import {
  Avatar,
  Button,
  ButtonProps,
  CommunityIcon,
  CopyrightIcon,
  DarkPurpleGradient,
  Dialog,
  DollarCircleIcon,
  FrameCornersIcon,
  H1,
  H2,
  H2Props,
  H3,
  HashTagIcon,
  Heading,
  Image,
  InlineLink,
  isWeb,
  Link,
  LinkButton,
  LinkButtonProps,
  Page as PageCore,
  PageLoading,
  PagePortableText,
  Paragraph,
  ParagraphProps,
  PlayIcon,
  ProhibitIcon,
  SectionProps,
  Sheet,
  Spinner,
  StopWatchIcon,
  StorefrontIcon,
  Tag,
  Text,
  ThumbsDownIcon,
  toast,
  useAnalytics,
  useMedia,
  XStack,
  YStack,
  YStackProps,
} from '@bounty/creators-design-system'
import {
  isNotNil,
  prettyCurrency,
  prettyFloatingDate,
  UnreachableCaseError,
} from '@bounty/utils'

import BountyAcceptBrandedContent from '../../assets/bounty-accept-branded-content.png'
import BountyAcceptPostInstructions from '../../assets/bounty-accept-post-instructions.png'
import BountyBrandmark from '../../assets/bounty-brandmark.png'
import HowBountyWorks from '../../assets/how-bounty-works.png'
import InstagramLogo from '../../assets/instagram.png'
import TikTokLogo from '../../assets/tiktok.png'
import { useFastForm } from '../../components'
import { ReceivedMessageItem } from '../../components/messages/MessageItem'
import { ProductTile as ProductTileCore } from '../../components/ProductTile'
import { RetailLocationsModal } from '../../components/retail/RetailLocationsModal'
import { PUBLIC_ASSETS_BUCKET_URL } from '../../config/env'
import { BOUNTY_CONTENT_TYPES } from '../../const'
import { createStateContext } from '../../factory'
import {
  AcceptSparkAdCodeRequestDocument,
  BountyDetailDocument,
  BountyDetailQuery,
  BountyListDocument,
  CreateCrossPostBountyDocument,
  ForceCheckForNewVideoDocument,
  HomeDocument,
  MarkBountyOrderDeliveredDocument,
  RequestExtensionForBountyDocument,
  SelectableBriefsDocument,
  SocialProfileType,
  UserBountyContentType,
  UserPayment_Status,
} from '../../generated/backendGraphql'
import { useSWRSanity } from '../../hooks/swr'
import { captureException } from '../../libs/sentry/sentry'
import { useVideoPlayerState } from '../../provider/videoPlayer'
import { logger } from '../../utils/logger'
import {
  getImageForInstagramReelContentId,
  getImageForTikTokVideoId,
  getInstagramProfileImageForUser,
  getTikTokProfileImageForUser,
  getVideoForTikTokVideoId,
} from '../../utils/videoHelpers'
import { CallToAction } from '../home/components/CallToAction'
import { useAcceptBounty } from './useAcceptBounty'
import { contentType } from './utils/contentType'
import {
  isAvailableToExtendBountyDateValid,
  shouldShowExpirationExtensionButton,
} from './utils/extensionUtils'
import {
  constructFeaturesOrFallback,
  fallbackTo,
} from './utils/productInformationFallbacks'
import useHyperlinkParser from '../../../hooks/src/useHyperlinkParser'

export const [BountyContext, useBountyContext] = createStateContext<{
  bounty: BountyDetailQuery['bounty'] | null
  tikTokProfile: BountyDetailQuery['tikTokProfile'] | null
  instagramProfile: BountyDetailQuery['instagramProfile'] | null
  showCommunityRules: boolean
}>({
  bounty: null,
  tikTokProfile: null,
  instagramProfile: null,
  showCommunityRules: false,
})

type UnifiedSocialProfileData = {
  profileName: string
  profileLink: string
  profileType: SocialProfileType
  profileImage: string
}

export const useBounty = () => {
  const [{ bounty }] = useBountyContext()
  if (!bounty) {
    throw new Error(
      'Cannot use the useBounty hook with no Bounty. Make sure the query is completed and successful!',
    )
  }

  return bounty
}

export const useSocialProfiles = () => {
  const [{ tikTokProfile, instagramProfile }] = useBountyContext()

  return { tikTokProfile, instagramProfile }
}

export const useAppropriateSocialProfile = (): UnifiedSocialProfileData => {
  const [{ bounty, tikTokProfile, instagramProfile }] = useBountyContext()

  if (bounty?.contentTypeSubmitted === 'TIKTOK_VIDEO' && tikTokProfile) {
    return {
      profileName: tikTokProfile.profileName!,
      profileLink: 'https://www.tiktok.com/' + tikTokProfile.profileName,
      profileType: tikTokProfile.type,
      profileImage: getTikTokProfileImageForUser(bounty.userId),
    }
  }

  if (bounty?.contentTypeSubmitted === 'INSTAGRAM_REEL' && instagramProfile) {
    return {
      profileName: instagramProfile.profileName!,
      profileLink: instagramProfile.profileLink!,
      profileType: instagramProfile.type,
      profileImage: getInstagramProfileImageForUser(bounty.userId),
    }
  }

  logger.error(
    "Couldn't determine appropriate social profile for bounty",
    bounty,
    tikTokProfile,
    instagramProfile,
  )
  throw new Error(
    'Unknown bounty content type submitted. Cannot determine appropriate social profile.',
  )
}

export const useShowCommunityRules = () => {
  const [{ showCommunityRules }, setBountyContext] = useBountyContext()

  const setShowCommunityRules = (showCommunityRules: boolean) => {
    setBountyContext((x) => ({ ...x, showCommunityRules }))
  }
  return [showCommunityRules, setShowCommunityRules] as const
}

type BountyStatusCardProps = YStackProps

export const StatusHeading = ({ children }: { children: ReactNode }) => {
  return (
    <Heading size="$md" color="$neutral.50">
      {children}
    </Heading>
  )
}

export const StatusParagraph = ({ children }: { children: ReactNode }) => {
  return (
    <Paragraph size="$sm" color="$neutral.50">
      {children}
    </Paragraph>
  )
}

export const StatusButton = (props: ButtonProps) => {
  return (
    <Button
      mt="$2"
      variant="ghost"
      backgroundColor="$neutral.50"
      size="$sm"
      {...props}
    />
  )
}

export const StatusButtonLink = (props: LinkButtonProps) => {
  return (
    <LinkButton
      mt="$2"
      variant="ghost"
      backgroundColor="$neutral.50"
      size="$sm"
      {...props}
    />
  )
}

export const StatusCard = (props: BountyStatusCardProps) => {
  return (
    <YStack
      space="$2"
      backgroundColor={'$primary.600'}
      borderRadius="$3"
      py="$3"
      px="$4"
      {...props}
    />
  )
}

export const HowBountyWorksCard = () => {
  const [, setVideoPlayerState] = useVideoPlayerState()

  return (
    <XStack
      borderRadius={'$3'}
      borderWidth={1}
      borderColor="$neutral.200"
      px="$4"
      mt="$4"
      alignItems="center"
      justifyContent="space-between"
      backgroundColor={'$neutral.50'}
    >
      <YStack space="$2" alignItems="flex-start" justifyContent="center">
        <Heading size="$md" color="rgba(92, 225, 230, 1)">
          How Bounty Works
        </Heading>
        {/* TODO: Add Links to watch */}
        <Button
          event="Bounty Detail Watch Intro Clicked"
          size="$xs"
          iconLeft={PlayIcon}
          iconPropsLeft={{ weight: 'fill' }}
          onPress={() => {
            setVideoPlayerState((x) => ({
              ...x,
              videoUrl: `${PUBLIC_ASSETS_BUCKET_URL}/misc-assets/bounty-post-tutorial.mp4`,
              poster: `${PUBLIC_ASSETS_BUCKET_URL}/misc-assets/bounty-post-tutorial.jpg`,
              videoConfig: {
                muted: false,
              },
            }))
          }}
        >
          Watch Intro
        </Button>
      </YStack>
      <XStack pt="$6">
        <Image
          src={HowBountyWorks}
          height={108}
          width={92}
          onPress={() => {
            setVideoPlayerState((x) => ({
              ...x,
              videoUrl: `${PUBLIC_ASSETS_BUCKET_URL}/misc-assets/bounty-post-tutorial.mp4`,
              poster: `${PUBLIC_ASSETS_BUCKET_URL}/misc-assets/bounty-post-tutorial.jpg`,
              videoConfig: {
                muted: false,
              },
            }))
          }}
        />
      </XStack>
    </XStack>
  )
}

export const RetailDetailsCard = () => {
  const bounty = useBounty()
  const [showRetailLocations, setShowRetailLocations] = useState(false)

  return (
    <YStack
      space="$3"
      alignItems="flex-start"
      borderRadius={'$3'}
      borderWidth={1}
      borderColor="$neutral.200"
      mt="$5"
      p="$4"
      justifyContent="space-between"
      backgroundColor={'$neutral.50'}
    >
      <StorefrontIcon weight="bold" color="rgba(92, 225, 230, 1)" size={32} />
      <Heading size="$md">In-store</Heading>
      <Paragraph size="$md">
        Purchase <Text fontWeight="$bold">{bounty.store.name}</Text> in-store to
        start earning. Brands love it when you make videos purchasing the
        product, so feel free to make the video while you’re buying!
      </Paragraph>
      {/* TODO: Add links to storefont once added */}
      <Button
        onPress={() => setShowRetailLocations(true)}
        event="Bounty Detail Find Stores"
        size="$md"
      >
        Where to find {bounty.store.name}
      </Button>
      <RetailLocationsModal
        shopName={bounty.store.name}
        open={showRetailLocations}
        onOpenChange={(value) => setShowRetailLocations(value)}
        locations={[...bounty.store.retailLocations]}
        retailLocatorUrl={bounty.store.retailLocatorUrl}
      />
    </YStack>
  )
}

export const RulesVideoCard = () => {
  const [, setVideoPlayerState] = useVideoPlayerState()

  return (
    <SectionGroup innerWrapperProps={{ px: 0, py: 0, borderWidth: 0 }}>
      <XStack
        borderRadius={'$3'}
        borderWidth={1}
        borderColor="$neutral.200"
        px="$4"
        alignItems="center"
        justifyContent="space-between"
        position="relative"
        overflow="hidden"
      >
        <DarkPurpleGradient />
        <YStack
          alignSelf="center"
          width={160}
          zIndex={10}
          space="$2"
          alignItems="flex-start"
        >
          <Heading color="$neutral.50" size="$md" mb="$2">
            Follow these rules to get paid.
          </Heading>
          <Button
            event="Bounty Detail Watch Intro Clicked"
            size="$xs"
            colorScheme="whiteAlpha"
            bg="$neutral.50"
            buttonTextProps={{ color: 'rgba(92, 225, 230, 1)' }}
            onPress={() => {
              setVideoPlayerState((x) => ({
                ...x,
                videoUrl: `${PUBLIC_ASSETS_BUCKET_URL}/misc-assets/bounty-post-rules.mp4`,
                poster: `${PUBLIC_ASSETS_BUCKET_URL}/misc-assets/bounty-post-rules.jpg`,
                videoConfig: {
                  muted: false,
                },
              }))
            }}
          >
            Watch Quick Intro
          </Button>
        </YStack>
        <XStack pt="$4">
          <Image src={HowBountyWorks} height={108} width={92} />
        </XStack>
      </XStack>
    </SectionGroup>
  )
}

export const SectionGroup = ({
  children,
  innerWrapperProps,
  sectionProps,
}: {
  children: ReactNode
  innerWrapperProps?: YStackProps
  sectionProps?: SectionProps
}) => {
  return (
    <YStack {...sectionProps}>
      <YStack
        borderRadius={'$3'}
        backgroundColor={'$neutral.100'}
        borderWidth={1}
        borderColor="$neutral.200"
        p="$4"
        py="$6"
        mb="$3"
        {...innerWrapperProps}
      >
        {children}
      </YStack>
    </YStack>
  )
}

export const SectionGroupTitle = (props: H2Props) => {
  return <H2 size="$xl" {...props} />
}

export const SectionGroupDescription = (props: ParagraphProps) => {
  return <Paragraph size="$sm" color="$neutral.600" {...props} />
}

export const TitleRow = () => {
  const bounty = useBounty()

  return (
    <XStack space="$2" alignItems="center" mb="$4">
      <Avatar src={bounty.store.logoImgLink ?? BountyBrandmark} size={36} />
      <H1 size="$xl">{bounty.store.name} Bounty</H1>
    </XStack>
  )
}

export type PageProps = {
  children: ReactNode
  data: BountyDetailQuery
}

export const Page = ({ children, data }: PageProps) => {
  const memoContext = useMemo(
    () => ({
      bounty: data.bounty,
      tikTokProfile: data.tikTokProfile,
      instagramProfile: data.instagramProfile,
      showCommunityRules: false,
    }),
    [data.bounty, data.tikTokProfile, data.instagramProfile],
  )

  return (
    <BountyContext shouldUpdateOnInitialValueChange initialValue={memoContext}>
      <PageCore scroll containerSize="sm">
        {children}
      </PageCore>
      <CommunityRulesSheet />
      <CallToActionDialog />
    </BountyContext>
  )
}

export const CallToActionDialog = () => {
  const [showCTA, setShowCTA] = useState(false)

  const { data, loading } = useQuery(HomeDocument, {
    variables: { params: { skip: 0, take: 1 } },
  })

  useEffect(() => {
    if (data) {
      const firstBounty = data.bounties.items[0]

      const showCTA =
        data.bounties.count === 1 &&
        data.currentUser.communities?.includes(firstBounty.store.id) &&
        ['PENDING', 'ACCEPTED', 'LIVE', 'COMPLETED', 'HOLD'].includes(
          firstBounty.status,
        )

      setShowCTA(showCTA ?? false)
    }
  }, [data])

  if (loading || !data) return null

  const firstBounty = data.bounties.items[0]

  if (!firstBounty) return null

  return (
    <Dialog
      dialogContentProps={{
        maxWidth: 500,
      }}
      sheetProps={{
        snapPoints: [40],
      }}
      open={showCTA}
      onOpenChange={setShowCTA}
    >
      <CallToAction
        storeLogoImgLink={firstBounty.store.logoImgLink ?? BountyBrandmark}
        storeName={firstBounty.store.name}
        onPress={() => setShowCTA(false)}
        containerProps={{ bordered: 0 }}
      />
    </Dialog>
  )
}

export const MinimumPayout = () => {
  const bounty = useBounty()
  if (
    !bounty.minimumPayoutInformation ||
    bounty.minimumPayoutInformation.isEligibleForMinimumPayout === false ||
    !bounty.paymentSnapshot?.productGiftConfig?.hasMinPayment
  ) {
    return null
  }

  return (
    <YStack mb="$6">
      <YStack
        py="$3"
        px="$4"
        borderRadius={'$3'}
        backgroundColor="$success.200"
      >
        <Paragraph
          data-testid="minimumPayoutAmount"
          size="$sm"
          color="$success.900"
          fontWeight={'$bold'}
        >
          You're eligible for a minimum payout of{' '}
          {prettyCurrency(bounty.minimumPayoutInformation.minimumPayoutAmount)}{' '}
          on this Bounty since it's your first time posting for{' '}
          {bounty.store.name}!
        </Paragraph>
        {/* <Paragraph size="$sm" color="$success.900">
          See how bounties are paid out.
        </Paragraph> */}
      </YStack>
    </YStack>
  )
}

const getPrettyPaymentStatus = (status: UserPayment_Status | undefined) => {
  switch (status) {
    case 'APPROVED':
      return 'Approved'
    case 'DENIED':
      return 'Denied'
    case 'FAILED':
      return 'Failed'
    case 'PENDING':
      return 'Processing'
    case 'SETTLED':
      return 'Completed'
    default:
      return 'Unknown'
  }
}

export const Earnings = () => {
  const bounty = useBounty()

  if (isNil(bounty.userPayment?.status)) {
    logger.log('Cannot use earnings component without a userPayment object.')
    return null
  }

  return (
    <SectionGroup>
      <SectionGroupTitle>Earnings</SectionGroupTitle>
      <SectionGroupDescription mb="$4">
        Payment Status:{' '}
        <Text data-testid="paymentStatus" size="$sm" fontWeight={'$semibold'}>
          {getPrettyPaymentStatus(bounty.userPayment?.status)}
        </Text>
      </SectionGroupDescription>
      <YStack
        py="$3"
        px="$4"
        borderRadius={'$3'}
        backgroundColor="$success.200"
      >
        <Heading
          data-testid="earnings"
          size="$2xl"
          color="$success.900"
          fontWeight={'$bold'}
        >
          {prettyCurrency(bounty.userPayment?.value ?? 0, { precision: 2 })}
        </Heading>
        {bounty.userPayment?.status === 'APPROVED' && (
          <Paragraph
            data-testid="fundsWillBeAvailableBy"
            mt="$1"
            size="$sm"
            color="$success.800"
          >
            Funds will be available by{' '}
            <Text color="$success.800" size="$sm" fontWeight="$semibold">
              {prettyFloatingDate(
                bounty.userPayment?.estimatedFundsAvailableDate.toString(),
              )}
            </Text>
            .
          </Paragraph>
        )}
      </YStack>
    </SectionGroup>
  )
}

export const CrossPostToOtherSocials = () => {
  const bounty = useBounty()

  const contentTypesAvailable = bounty.contentTypesAllowed?.filter(
    (x) => x !== bounty.contentTypeSubmitted,
  )

  if (contentTypesAvailable?.length === 0) return null

  const canCrossPostVideoToTikTok =
    contentTypesAvailable?.includes('TIKTOK_VIDEO')

  const canCrossPostReelToInstagram =
    contentTypesAvailable?.includes('INSTAGRAM_REEL')

  if (!canCrossPostVideoToTikTok && !canCrossPostReelToInstagram) return null

  return (
    <YStack>
      {canCrossPostVideoToTikTok && <CrossPostVideoToTikTok />}
      {canCrossPostReelToInstagram && <CrossPostReelToInstagram />}
    </YStack>
  )
}

type CrossPostToOtherSocialProps = {
  socialLogo: string
  heading: string
  buttonText: string
  buttonEventName: string
  buttonPress: string | (() => void)
  isLoading?: boolean
}

export const CrossPostToOtherSocial = ({
  socialLogo,
  heading,
  buttonText,
  buttonEventName,
  buttonPress,
  isLoading,
}: CrossPostToOtherSocialProps) => (
  <YStack
    mt="$4"
    borderRadius={'$3'}
    borderWidth={1}
    borderColor="$neutral.200"
    backgroundColor="white"
    px="$4"
    py="$6"
    space="$4"
  >
    <XStack space="$2" ai="center">
      <Image src={socialLogo} width={40} height={40} />
      <Heading>{heading}</Heading>
    </XStack>
    {typeof buttonPress === 'string' ? (
      <LinkButton event={buttonEventName} href={buttonPress}>
        {buttonText}
      </LinkButton>
    ) : (
      <Button
        event={buttonEventName}
        onPress={buttonPress}
        isLoading={isLoading}
      >
        {buttonText}
      </Button>
    )}
  </YStack>
)

export const CrossPostVideoToTikTok = () => {
  const bounty = useBounty()
  const { tikTokProfile } = useSocialProfiles()

  const [createCrossPostBounty, { loading, data }] = useMutation(
    CreateCrossPostBountyDocument,
    {
      variables: {
        bountyId: bounty.id,
        bountyContentType: 'TIKTOK_VIDEO',
      },
    },
  )

  return (
    <CrossPostToOtherSocial
      socialLogo={TikTokLogo}
      heading={
        data
          ? 'We created a new Bounty for your TikTok.'
          : 'Earn more if you post on TikTok too!'
      }
      buttonText={
        tikTokProfile && tikTokProfile.verified
          ? data
            ? 'Tap here to view'
            : 'Post a TikTok'
          : 'Verify your TikTok'
      }
      buttonEventName={
        data
          ? 'Bounty Detail View TikTok Video Clicked'
          : 'Bounty Detail Post To TikTok Clicked'
      }
      buttonPress={
        tikTokProfile && tikTokProfile.verified
          ? data
            ? `/bounties/${data.createCrossPostBounty}`
            : createCrossPostBounty
          : '/verify-tiktok'
      }
      isLoading={loading}
    />
  )
}

export const CrossPostReelToInstagram = () => {
  const bounty = useBounty()
  const { instagramProfile } = useSocialProfiles()

  const [createCrossPostBounty, { loading, data }] = useMutation(
    CreateCrossPostBountyDocument,
    {
      variables: {
        bountyId: bounty.id,
        bountyContentType: 'INSTAGRAM_REEL',
      },
    },
  )

  return (
    <CrossPostToOtherSocial
      socialLogo={InstagramLogo}
      heading={
        data
          ? 'We created a new Bounty for your Reel.'
          : 'Earn more if you post on Instagram too!'
      }
      buttonText={
        instagramProfile && instagramProfile.verified
          ? data
            ? 'Tap here to view'
            : 'Post a Reel'
          : 'Verify your Instagram'
      }
      buttonEventName={
        data
          ? 'Bounty Detail View Instagram Reel Clicked'
          : 'Bounty Detail Post To Instagram Reel Clicked'
      }
      buttonPress={
        instagramProfile && instagramProfile.verified
          ? data
            ? `/bounties/${data.createCrossPostBounty}`
            : createCrossPostBounty
          : '/verify-instagram'
      }
      isLoading={loading}
    />
  )
}

// Keep this component in sync with the live preview
export const SearchingForVideoPreview = () => {
  const bounty = useBounty()
  const socialProfile = useAppropriateSocialProfile()
  const [checkForNewVideoMutation, { loading, data }] = useMutation(
    ForceCheckForNewVideoDocument,
    {
      variables: {
        bountyId: bounty.id,
      },
      refetchQueries: [
        {
          query: BountyDetailDocument,
          variables: { bountyId: bounty.id },
        },
      ],
    },
  )

  useEffect(() => {
    if (data && data.forceCheckForNewVideo.code === 'VIDEO_FOUND') {
      toast.success({
        title: `${contentType(bounty.contentTypeSubmitted)} Detected!`,
        message:
          'You are all set! We will notify you when your bounty completes.',
      })
    }
  }, [bounty.contentTypeSubmitted, data])

  const getMessageForBounty = () => {
    if (!data) {
      return (
        <YStack>
          <Heading>
            Waiting for {contentType(bounty.contentTypeSubmitted)} post
          </Heading>
          <Paragraph size="$sm" flexShrink={1} flexWrap="wrap" mb="$2">
            We’ll text you within{' '}
            <Text size="$sm" fontWeight={'$semibold'}>
              1 hour
            </Text>{' '}
            to confirm your post is being tracked. Make sure to tag{' '}
            <Text fontWeight={'$semibold'} size="$sm">
              {bounty.contentTypeSubmitted === 'INSTAGRAM_REEL'
                ? bounty.store.instagramUsername
                : bounty.store.tikTokUsername}
            </Text>{' '}
            in the{' '}
            <Text fontWeight={'$semibold'} size="$sm">
              caption
            </Text>{' '}
            of your video for it to be detected.
          </Paragraph>
        </YStack>
      )
    }

    switch (data.forceCheckForNewVideo.code) {
      case 'ERROR':
        return (
          <YStack>
            <Heading>Error</Heading>
            <Paragraph size="$sm" flexShrink={1} flexWrap="wrap" mb="$2">
              Video could not be associated for an unknown reason. Please reach
              out to support for more information.
            </Paragraph>
          </YStack>
        )
      case 'RATE_LIMIT':
        return (
          <YStack>
            <Heading>Rate Limit Exceeded</Heading>
            <Paragraph size="$sm" flexShrink={1} flexWrap="wrap" mb="$2">
              Please wait a few minutes and try again.
            </Paragraph>
          </YStack>
        )
      case 'VIDEO_NOT_FOUND':
        return (
          <YStack>
            <Heading>Video Not Found</Heading>
            <Paragraph
              size="$sm"
              flexShrink={1}
              flexWrap="wrap"
              mb="$2"
              whiteSpace="break-spaces"
            >
              We could not find an eligible video for account{' '}
              <Text fontWeight={'$semibold'} size="$sm">
                {socialProfile.profileName}
              </Text>
              . Did you tag the brand{' '}
              <Text fontWeight={'$semibold'} size="$sm">
                {bounty.contentTypeSubmitted === 'INSTAGRAM_REEL'
                  ? bounty.store.instagramUsername
                  : bounty.store.tikTokUsername}
              </Text>{' '}
              in the caption of your video?
            </Paragraph>
          </YStack>
        )
      case 'VIDEO_FOUND':
        return null

      default:
        throw new UnreachableCaseError(data.forceCheckForNewVideo.code)
    }
  }

  // If a bounty is expired they need to request more time before seeing this component
  if (bounty.isExpired) return null

  return (
    <YStack
      mt="$4"
      borderRadius={'$3'}
      borderWidth={1}
      borderColor="$neutral.200"
      backgroundColor="white"
      px="$4"
      py="$6"
      space="$4"
    >
      <XStack space="$4">
        <XStack flexShrink={0}>
          <XStack
            borderRadius={'$3'}
            borderWidth={3}
            backgroundColor="$primary.50"
            borderColor="rgba(92, 225, 230, 1)"
            width={92}
            height={166}
            alignItems="center"
            justifyContent="center"
          >
            <Spinner />
          </XStack>
        </XStack>
        <YStack justifyContent="center" flexShrink={1} space="$2">
          {getMessageForBounty()}
          <Button
            event="Bounty Overview Check Now Button Clicked"
            alignSelf="flex-start"
            size="$sm"
            iconLeft={StopWatchIcon}
            iconPropsLeft={{ weight: 'fill' }}
            isLoading={loading}
            onPress={() => {
              checkForNewVideoMutation()
            }}
          >
            Link my video
          </Button>
        </YStack>
      </XStack>
    </YStack>
  )
}

const getBountyContentDataForContentType = (
  bounty: BountyDetailQuery['bounty'],
) => {
  if (bounty.contentTypeSubmitted === 'TIKTOK_VIDEO' && bounty.tikTokVideo) {
    return {
      contentId: bounty.tikTokVideo.videoId,
      contentUrl: bounty.tikTokVideo.videoUrl,
      title: bounty.tikTokVideo.videoTitle,
      thumbnail: getImageForTikTokVideoId(bounty.tikTokVideo.videoId),
    }
  }
  if (
    bounty.contentTypeSubmitted === 'INSTAGRAM_REEL' &&
    bounty.instagramContent
  ) {
    return {
      contentId: bounty.instagramContent.contentId,
      contentUrl: bounty.instagramContent.url,
      title: bounty.instagramContent.title,
      thumbnail: getImageForInstagramReelContentId(
        bounty.instagramContent.contentId,
      ),
    }
  }

  return null
}

// Keep this component in sync with the search for preview
export const LiveVideoPreview = () => {
  const bounty = useBounty()
  const socialProfileData = useAppropriateSocialProfile()
  const { track } = useAnalytics()
  const contentData = getBountyContentDataForContentType(bounty)
  if (contentData === null) {
    return <Text>Unable to render this content type :(</Text>
  }

  return (
    <YStack
      borderRadius={'$3'}
      borderWidth={1}
      borderColor="$neutral.200"
      backgroundColor={'$neutral.50'}
      mt="$6"
      px="$4"
      py="$6"
      space="$4"
    >
      <XStack space="$4">
        <XStack flexShrink={0}>
          <Link
            href={contentData.contentUrl}
            event="Bounty Detail Live Video Preview Image Clicked"
          >
            <Image
              borderRadius={'$3'}
              borderWidth={3}
              borderColor="rgba(92, 225, 230, 1)"
              width={92}
              height={166}
              resizeMode="cover"
              alt={contentData.title}
              src={contentData.thumbnail}
              cursor="pointer"
              onPress={() => {
                track('Bounty Live Video Card Watch Video Clicked', {
                  imageUrl: contentData.thumbnail,
                  location: 'IMAGE',
                })
              }}
            />
          </Link>
        </XStack>
        <YStack justifyContent="center" flexShrink={1}>
          <XStack mb="$2" alignItems="center" flexShrink={1}>
            <Avatar size={18} src={socialProfileData.profileImage} mr="$1" />
            <Text
              flexShrink={1}
              numberOfLines={1}
              size="$sm"
              fontWeight={'$semibold'}
            >
              {socialProfileData.profileName}
            </Text>
          </XStack>
          <Paragraph
            size="$sm"
            flexShrink={1}
            numberOfLines={3}
            flexWrap="wrap"
            mb="$2"
          >
            {contentData.title}
          </Paragraph>
          <Link
            href={contentData.contentUrl}
            event="Bounty Detail Live Video Preview Clicked"
          >
            <Button
              alignSelf="flex-start"
              size="$xs"
              iconLeft={PlayIcon}
              iconPropsLeft={{ weight: 'fill' }}
              event={{
                eventName: 'Bounty Live Video Card Watch Video Clicked',
                imageUrl: contentData.thumbnail,
                location: 'BUTTON',
              }}
            >
              Watch
            </Button>
          </Link>
        </YStack>
      </XStack>
      <Paragraph size="$sm" textAlign="center">
        Wrong video?{' '}
        <InlineLink
          event="Bounty Detail Live Video Wrong Video Clicked"
          href="mailto:helpdesk@gen.video"
          size="$sm"
        >
          Let us know
        </InlineLink>
      </Paragraph>
    </YStack>
  )
}

const WeightedText = ({ children }: { children: ReactNode }) => {
  return (
    <Text
      size="$sm"
      fontWeight={'$semibold'}
      ref={(node) => {
        if (isWeb && node) {
          node.style.display = 'inline'
        }
      }}
    >
      {children}
    </Text>
  )
}

const guidelineVideos = [
  {
    videoId: '7184082841544756522',
    content: (
      <YStack space="$3">
        <Text size="$sm" whiteSpace="break-spaces">
          This is a great example of{' '}
          <WeightedText>good lighting and camera presence</WeightedText>.
        </Text>
        <Text size="$sm" whiteSpace="break-spaces">
          It’s also <WeightedText>less than a minute long</WeightedText>.
        </Text>
      </YStack>
    ),
  },
  {
    videoId: '7191584695724002606',
    content: (
      <YStack space="$3">
        <Text size="$sm" whiteSpace="break-spaces">
          This video is a terrific example of a{' '}
          <WeightedText>hook</WeightedText>.
        </Text>
        <Text size="$sm" whiteSpace="break-spaces">
          It also has{' '}
          <WeightedText>good camera presence, lighting, and audio</WeightedText>
          .
        </Text>
      </YStack>
    ),
  },
  {
    videoId: '7202336124700937518',
    content: (
      <YStack space="$3">
        <Text size="$sm" whiteSpace="break-spaces">
          This creator <WeightedText>explains the benefits</WeightedText> of the
          specific product she’s using with a visual demonstration.
        </Text>
        <Text size="$sm" whiteSpace="break-spaces">
          The lighting in the video is also{' '}
          <WeightedText>bright and glare-free</WeightedText>.
        </Text>
      </YStack>
    ),
  },
  {
    videoId: '7183093141841628462',
    content: (
      <YStack space="$3">
        <Text size="$sm" whiteSpace="break-spaces">
          This video incorporates the creator’s{' '}
          <WeightedText>personal experience</WeightedText> with the content into
          the content.
        </Text>
        <Text size="$sm" whiteSpace="break-spaces">
          She also tries out the <WeightedText>product on camera</WeightedText>.
        </Text>
        <Text size="$sm" whiteSpace="break-spaces">
          <WeightedText>Tip:</WeightedText> It’s great to say you bought the
          product. Avoid saying you were not sponsored.
        </Text>
      </YStack>
    ),
  },
  {
    videoId: '7195567289255709994',
    content: (
      <YStack space="$3">
        <Text size="$sm" whiteSpace="break-spaces">
          This video lists some{' '}
          <WeightedText>positives of the product</WeightedText>. It’s also{' '}
          <WeightedText>short and to the point</WeightedText>.
        </Text>
        <Text size="$sm" whiteSpace="break-spaces">
          The <WeightedText>fast cuts between clips</WeightedText> keep the
          viewer watching.
        </Text>
      </YStack>
    ),
  },
  {
    videoId: '7186361137506635054',
    content: (
      <YStack space="$3">
        <Text size="$sm" whiteSpace="break-spaces">
          This video shows a{' '}
          <WeightedText>before and after of the product</WeightedText>.
        </Text>
        <Text size="$sm" whiteSpace="break-spaces">
          <WeightedText>Good lighting and audio</WeightedText> are also positive
          aspects of this video.
        </Text>
      </YStack>
    ),
  },
  {
    videoId: '7134519030592982315',
    content: (
      <YStack space="$3">
        <Text size="$sm" whiteSpace="break-spaces">
          This video is <WeightedText>unique and creative</WeightedText>.
        </Text>
        <Text size="$sm" whiteSpace="break-spaces">
          It also has <WeightedText>good lighting</WeightedText>, while
          providing a <WeightedText>visual demonstration</WeightedText> of the
          benefits of the product.
        </Text>
      </YStack>
    ),
  },
  {
    videoId: '7191341770301508906',
    content: (
      <YStack space="$3">
        <Text size="$sm" whiteSpace="break-spaces">
          This video <WeightedText>demonstrates the product</WeightedText>. It
          also has <WeightedText>good lighting </WeightedText>
          and the <WeightedText>creator is talking to the camera</WeightedText>.
        </Text>
      </YStack>
    ),
  },
]

export const GuidelineCard = ({
  imageUrl,
  videoUrl,
  content,
}: {
  imageUrl: string
  videoUrl: string
  content: ReactNode
}) => {
  const { track } = useAnalytics()
  const [, setVideoPlayerState] = useVideoPlayerState()
  return (
    <YStack mb="$6">
      <YStack
        borderRadius={'$3'}
        borderWidth={1}
        borderColor="$neutral.200"
        backgroundColor={'$neutral.50'}
        px="$4"
        py="$6"
        space="$4"
      >
        <XStack space="$4">
          <XStack flexShrink={0}>
            <Image
              borderRadius={'$3'}
              borderWidth={3}
              borderColor="rgba(92, 225, 230, 1)"
              width={92}
              height={166}
              resizeMode="cover"
              src={imageUrl}
              cursor="pointer"
              onPress={() => {
                track('Bounty Guideline Card Watch Video Clicked', {
                  imageUrl,
                  location: 'IMAGE',
                })
                setVideoPlayerState((x) => ({
                  ...x,
                  videoUrl,
                  poster: imageUrl,
                  videoConfig: {
                    muted: false,
                  },
                }))
              }}
            />
          </XStack>
          <YStack justifyContent="center" flexShrink={1}>
            <YStack mb="$2">{content}</YStack>
            <Button
              event={{
                eventName: 'Bounty Guideline Card Watch Video Clicked',
                imageUrl,
                location: 'BUTTON',
              }}
              alignSelf="flex-start"
              size="$xs"
              iconLeft={PlayIcon}
              iconPropsLeft={{ weight: 'fill' }}
              onPress={() => {
                setVideoPlayerState((x) => ({
                  ...x,
                  videoUrl,
                  poster: imageUrl,
                  videoConfig: {
                    muted: false,
                  },
                }))
              }}
            >
              Watch
            </Button>
          </YStack>
        </XStack>
      </YStack>
    </YStack>
  )
}

export const Guidelines = () => {
  const bounty = useBounty()

  /**
   * This shims custom inspiration videos for brands. We are putting this in to hold us over until
   * we formally support this feature.
   *
   * @deprecated
   */
  const doCustomInspirationVideoShim = (): {
    videoId: string
    videoUrlOverride?: string
    imageUrlOverride?: string
    content: ReactNode
  }[] => {
    // Blendjet
    if (bounty.store.id === '0aab00c3-06a9-45f6-ba30-43f86956cde3') {
      return [
        {
          videoId: 'test-1',
          videoUrlOverride:
            'https://d21jj9ods4nmqi.cloudfront.net/misc-assets/blendjet-custom-inspiration-video.mp4',
          imageUrlOverride:
            'https://d21jj9ods4nmqi.cloudfront.net/misc-assets/blendjet-custom-inspiration-video-thumbnail.png',
          content: (
            <YStack space="$3">
              <Text size="$sm" whiteSpace="break-spaces">
                This is a great example of explaining{' '}
                <WeightedText>key features</WeightedText>.
              </Text>
              <Text size="$sm" whiteSpace="break-spaces">
                It’s also{' '}
                <WeightedText>
                  shows a demonstration of the product
                </WeightedText>
                .
              </Text>
            </YStack>
          ),
        },
        {
          videoId: 'test-2',
          videoUrlOverride:
            'https://d21jj9ods4nmqi.cloudfront.net/misc-assets/blendjet-custom-inspiration-video-1.mp4',
          imageUrlOverride:
            'https://d21jj9ods4nmqi.cloudfront.net/misc-assets/blendjet-custom-inspiration-video-1-thumbnail.png',
          content: (
            <YStack space="$3">
              <Text size="$sm" whiteSpace="break-spaces">
                This video demonstrates{' '}
                <WeightedText>key differentiators</WeightedText> compared to
                other products.
              </Text>
              <Text size="$sm" whiteSpace="break-spaces">
                It’s also <WeightedText>has good lighting</WeightedText>.
              </Text>
            </YStack>
          ),
        },
        ...guidelineVideos,
      ]
    }

    return guidelineVideos
  }

  return (
    <YStack>
      <YStack mb="$3">
        <Heading size="$xl">Content Inspiration:</Heading>
      </YStack>
      {doCustomInspirationVideoShim().map((x) => {
        return (
          <GuidelineCard
            key={x.videoId}
            imageUrl={x.imageUrlOverride ?? getImageForTikTokVideoId(x.videoId)}
            videoUrl={x.videoUrlOverride ?? getVideoForTikTokVideoId(x.videoId)}
            content={x.content}
          />
        )
      })}
    </YStack>
  )
}

export const CommunityRules = () => {
  const [, setShow] = useShowCommunityRules()

  return (
    <SectionGroup innerWrapperProps={{ backgroundColor: 'white', py: '$4' }}>
      <SectionGroupTitle mb="$2" size="$md">
        Rules
      </SectionGroupTitle>
      <SectionGroupDescription mb="$2">
        All bounties must abide by our{' '}
        <Text
          size="$sm"
          color="$neutral.600"
          textDecorationLine="underline"
          cursor="pointer"
          accessibilityRole="link"
          onPress={(e) => {
            setShow(true)
          }}
        >
          community rules
        </Text>
        .
      </SectionGroupDescription>
      <Button
        colorScheme="primary"
        variant="solid"
        event={'Bounty Learn More Clicked'}
        onPress={(e) => {
          setShow(true)
        }}
      >
        Learn More
      </Button>
    </SectionGroup>
  )
}

export const Feedback = () => {
  const bounty = useBounty()
  return (
    <SectionGroup>
      <SectionGroupTitle mb="$2" size="$md">
        Feedback
      </SectionGroupTitle>
      <SectionGroupDescription data-testid="feedbackMessage">
        {bounty.rating?.feedbackMessage}
      </SectionGroupDescription>
    </SectionGroup>
  )
}

export const HowWasYourExperience = () => {
  return (
    <SectionGroup innerWrapperProps={{ backgroundColor: 'white', py: '$4' }}>
      <SectionGroupTitle mb="$2" size="$md">
        How can we improve?
      </SectionGroupTitle>
      <InlineLink
        size="$sm"
        color="$neutral.600"
        href="/feedback?ref=bountyDetail&notificationMedium=INAPP"
        event="Bounty Detail Feedback Redirect Clicked"
      >
        Let us know
      </InlineLink>
    </SectionGroup>
  )
}

export const IconText = ({
  icon: Icon,
  imageSrc,
  text,
  extra,
}: {
  icon?: ComponentType<IconProps>
  imageSrc?: string
  text: ReactNode
  extra?: ReactNode
}) => {
  return (
    <SectionGroup innerWrapperProps={{ backgroundColor: 'white', py: '$4' }}>
      <SectionGroupTitle
        display="flex"
        mb={extra ? '$2' : 0}
        size="$md"
        alignItems="center"
      >
        <XStack mr="$1" justifyContent="center" alignItems="center">
          {imageSrc && <Image src={imageSrc} width={28} height={28} />}
          {Icon && <Icon weight="bold" color="$neutral.900" size={22} />}
        </XStack>
        <Text>{text}</Text>
      </SectionGroupTitle>
      {extra ? extra : null}
    </SectionGroup>
  )
}

export const OrderDetails = () => {
  const bounty = useBounty()
  const [
    markBountyOrderDelivered,
    { loading: markBountyOrderDeliveredLoading },
  ] = useMutation(MarkBountyOrderDeliveredDocument, {
    variables: {
      bountyId: bounty.id,
    },
  })

  if (
    !bounty.order ||
    bounty.status !== 'PENDING' ||
    bounty.order.status === 'DELIVERED'
  )
    return null

  return (
    <SectionGroup>
      <XStack alignItems="center">
        <SectionGroupTitle flexGrow={1} flexShrink={1}>
          Order Details
        </SectionGroupTitle>

        {/* No way to get this from the backend */}
        {/* <LinkButton
          variant="link"
          size="$sm"
          flexShrink={0}
          colorScheme="neutral"
          href={``}
        >
          View order
        </LinkButton> */}
      </XStack>
      <SectionGroupDescription mb="$4">
        Received your package? Click mark delivered to get started on your
        Bounty!
      </SectionGroupDescription>
      <Button
        isLoading={markBountyOrderDeliveredLoading}
        size="$sm"
        event="Bounty Order Details Card Mark Delivered Clicked"
        onPress={async () => {
          await markBountyOrderDelivered()

          toast.success({
            title: 'Order marked delivered',
            message: 'Accept your Bounty to start earning!',
          })
        }}
      >
        Mark Delivered
      </Button>
    </SectionGroup>
  )
}

export const RulePostingInstructions = () => {
  const bounty = useBounty()
  const { gtXs, gtSm, gtMd, gtLg } = useMedia()
  const imageAspectRatio = 1.74

  /**
   * Update the caption placement if you change these. We need it to map correctly so that
   * users can see the brand to tag in the caption below.
   */
  const getImageSize = () => {
    if (gtLg) {
      return { width: 480, height: 480 / imageAspectRatio }
    } else if (gtMd) {
      return { width: 480, height: 480 / imageAspectRatio }
    } else if (gtSm) {
      return { width: 450, height: 450 / imageAspectRatio }
    } else if (gtXs) {
      return { width: 420, height: 420 / imageAspectRatio }
    } else {
      return { width: 320, height: 320 / imageAspectRatio }
    }
  }

  const getCaptionPlacement = () => {
    if (gtLg) {
      return { top: 70, left: 10 }
    } else if (gtMd) {
      return { top: 70, left: 10 }
    } else if (gtSm) {
      return { top: 65, left: 10 }
    } else if (gtXs) {
      return { top: 60, left: 10 }
    } else {
      return { top: 40, left: 10 }
    }
  }

  return (
    <IconText
      imageSrc={TikTokLogo}
      text={
        <Text whiteSpace="break-spaces">
          <Text>Tag </Text>
          <Text
            textDecorationLine="underline"
            fontWeight={'$bold'}
            data-testid="postingInstructionsStoreText"
          >
            {bounty.store.tikTokUsername}
          </Text>
          <Text> in the caption.</Text>
        </Text>
      }
      extra={
        <YStack position="relative" {...getImageSize()}>
          <YStack position="absolute" zIndex={10} {...getCaptionPlacement()}>
            <Paragraph>
              <Text
                size="$xs"
                $gtSm={{ size: '$md' }}
                borderWidth={4}
                borderColor="rgba(92, 225, 230, 1)"
                borderRadius={'$2'}
                px="$1"
                fontWeight={'$semibold'}
                data-testid="postingInstructionsImageOverlay"
              >
                {bounty.store.tikTokUsername}
              </Text>
              <Text size="$xs" $gtSm={{ size: '$md' }} ml="$1">
                your caption...
              </Text>
            </Paragraph>
            <Heading
              pt="$1"
              color="rgba(92, 225, 230, 1)"
              fontWeight={'$bold'}
              size="$md"
              $gtSm={{ size: '$xl' }}
            >
              Tag here
            </Heading>
          </YStack>
          <Image
            width={'100%'}
            height={'100%'}
            src={BountyAcceptPostInstructions}
            resizeMode="contain"
            position="absolute"
            top={0}
            bottom={0}
            left={0}
            right={0}
          />
        </YStack>
      }
    />
  )
}

export const RuleBrandedContent = () => {
  const imageAspectRatio = 2.96
  const { gtXs, gtSm, gtMd, gtLg } = useMedia()

  const getImageSize = () => {
    if (gtLg) {
      return { width: 480, height: 480 / imageAspectRatio }
    } else if (gtMd) {
      return { width: 480, height: 480 / imageAspectRatio }
    } else if (gtSm) {
      return { width: 450, height: 450 / imageAspectRatio }
    } else if (gtXs) {
      return { width: 420, height: 420 / imageAspectRatio }
    } else {
      return { width: 320, height: 320 / imageAspectRatio }
    }
  }

  return (
    <IconText
      icon={DollarCircleIcon}
      text={'Turn on the branded content toggle.'}
      extra={
        <YStack position="relative" {...getImageSize()}>
          <Image
            width={'100%'}
            height={'100%'}
            src={BountyAcceptBrandedContent}
            resizeMode="contain"
            position="absolute"
            top={0}
            bottom={0}
            left={0}
            right={0}
          />
        </YStack>
      }
    />
  )
}

export const RuleNoProfanity = () => {
  return <IconText icon={ProhibitIcon} text={'No profanity.'} />
}

export const RuleHashtag = () => {
  const bounty = useBounty()

  return (
    <IconText
      icon={HashTagIcon as ComponentType<IconProps>}
      text={
        <Text whiteSpace="break-spaces">
          <Text>Required hashtags: </Text>
          <Text
            fontWeight={'$bold'}
            textDecorationLine="underline"
            data-testid="postingInstructionsHashtagText"
          >
            {bounty.store.brandedHashtag ?? '#bounty'}
          </Text>
        </Text>
      }
    />
  )
}

export const RuleTag = () => {
  const bounty = useBounty()

  return (
    <IconText
      imageSrc={InstagramLogo}
      text={
        <Text whiteSpace="break-spaces">
          <Text>Tag </Text>
          <Text
            textDecorationLine="underline"
            fontWeight={'$bold'}
            data-testid="postingInstructionsStoreTag"
          >
            {bounty.store.instagramUsername ?? '@bounty'}
          </Text>
          <Text> in your caption.</Text>
        </Text>
      }
    />
  )
}

export const RuleNoCopyrightedContent = () => {
  return (
    <IconText icon={CopyrightIcon} text={'No copyrighted sounds or content.'} />
  )
}

export const RuleProductFocus = () => {
  return (
    <IconText
      icon={FrameCornersIcon}
      text={'Product must be the focus of your video.'}
    />
  )
}

export const RuleAffiliateCode = () => {
  return (
    <IconText
      icon={ThumbsDownIcon}
      text={'No promoting referral codes. No tagging other brands.'}
    />
  )
}

export const RuleCommunityGuidelines = () => {
  return (
    <IconText
      icon={CommunityIcon}
      text={<Text>Must follow the Bounty community guidelines.</Text>}
    />
  )
}

export const RuleList = ({
  title = 'Or read the rules below',
}: {
  title?: string
}) => {
  const [{ bountyContentType }] = useAcceptBounty()

  return (
    <>
      <H3 size="$xl" mb="$4" mt="$2">
        {title}
      </H3>
      {/* Too complicated right now */}
      {/* <B.RuleBrandedContent /> */}
      {bountyContentType === 'TIKTOK_VIDEO' && <RulePostingInstructions />}
      {bountyContentType === 'INSTAGRAM_REEL' && <RuleTag />}
      <RuleHashtag />
      {/* <RuleNoProfanity /> */}
      {/* <RuleNoCopyrightedContent /> */}
      {/* <RuleProductFocus /> */}
      {/* Hiding the no referral code rule for Old School Labs  */}
      {/* {bounty.store.id !== '1ae07e4e-1ae7-46a4-9f07-616b228ea784f' && (
        <RuleAffiliateCode />
      )}
      <CommunityRules /> */}
    </>
  )
}

export const ExpiredCard = () => {
  const [requestExtensionForBounty, { loading }] = useMutation(
    RequestExtensionForBountyDocument,
  )
  const bounty = useBounty()

  const handleClickRequestExtension = async () => {
    if (isNil(bounty.extensionInformation?.availableToExtendDate)) {
      toast.error({
        title: `Extend bounty failed.`,
        message: `Sorry, your request to extend this Bounty failed. Please try again.`,
      })
      return
    }

    const extendAvailable = isAvailableToExtendBountyDateValid(
      bounty.extensionInformation?.availableToExtendDate,
    )

    if (extendAvailable) {
      const bountyId = bounty.id
      try {
        await requestExtensionForBounty({
          variables: {
            bountyId,
          },
          awaitRefetchQueries: true,
          refetchQueries: [
            { query: BountyDetailDocument, variables: { bountyId } },
            { query: BountyListDocument },
          ],
        })
        toast.success({
          title: 'Extension Granted',
          message: 'Accept your Bounty to get started.',
        })
      } catch (error) {
        logger.error(error)
        captureException(error)
      }
    } else {
      toast.error({
        title: 'Extension Failed',
        message: 'Could not extend Bounty. Please try again.',
      })
    }
  }

  return (
    <StatusCard>
      <StatusHeading>Ready to Post?</StatusHeading>
      <StatusParagraph>It's not too late.</StatusParagraph>
      {shouldShowExpirationExtensionButton(bounty) && (
        <StatusButton
          size="$sm"
          event="Bounty Status Card Request More Time Clicked"
          onPress={handleClickRequestExtension}
          isLoading={loading}
        >
          I need more time
        </StatusButton>
      )}
    </StatusCard>
  )
}

export const ProductTile = () => {
  const bounty = useBounty()

  if (!bounty.product) return null

  return (
    <ProductTileCore
      imgLink={bounty.product.imgLink}
      productName={bounty.product.productName}
    />
  )
}

export const BriefHeadline = () => {
  const bounty = useBounty()
  const headline =
    bounty.type === 'RETAIL'
      ? bounty.store.retailBrief?.headline
      : bounty.product?.productInstruction?.headline

  return (
    <YStack my="$4">
      <Text fontWeight={'$semibold'} size="$lg" data-testid="briefHeadline">
        {fallbackTo(headline, DEFAULT_PRODUCT_HEADLINE)}
      </Text>
    </YStack>
  )
}

export const SelectBriefTitle = () => {
  const bounty = useBounty()

  const { data } = useQuery(SelectableBriefsDocument, {
    variables: { storeId: bounty.store.id },
  })

  return (
    <YStack gap="$4" alignItems="center" mb="$8">
      <Avatar src={bounty.store.logoImgLink ?? BountyBrandmark} size={36} />
      <Text
        fontFamily="$heading"
        color="$primary.800"
        size="$2xl"
        textAlign="center"
      >
        {data && data.selectableBriefs.length > 0
          ? `Choose your Bounty for ${bounty.store.name}`
          : `${bounty.store.name} doesn’t have any Bounties right now`}
      </Text>
    </YStack>
  )
}

export const SelectableBriefList = () => {
  const bounty = useBounty()

  const [, setAcceptBounty] = useAcceptBounty()

  const { data, loading } = useQuery(SelectableBriefsDocument, {
    variables: { storeId: bounty.store.id },
  })

  if (loading || !data) return <Spinner />

  return (
    <YStack gap="$4">
      {data.selectableBriefs.length === 0 && (
        <Text color="$neutral.600">
          Check out our other brands to post and earn rewards
        </Text>
      )}
      {data.selectableBriefs.map((selectableBrief) => {
        return (
          <YStack
            key={selectableBrief.id}
            gap="$4"
            backgroundColor="white"
            borderColor="$neutral.200"
            borderWidth={1}
            borderRadius="$3"
            p="$4"
          >
            <XStack gap="$4" alignItems="center" justifyContent="space-between">
              <H3 size="$lg" numberOfLines={1}>
                {selectableBrief.name}
              </H3>
              <Tag
                colorScheme="blackAlpha"
                backgroundColor="white"
                textProps={{ fontFamily: '$heading', py: '$1' }}
              >
                ${selectableBrief.paymentSnapshot?.minBountyPayment ?? 0} Min
              </Tag>
            </XStack>
            <Text size="$sm">{selectableBrief.content}</Text>
            <XStack gap="$4">
              <LinkButton
                size="$sm"
                flexGrow={1}
                flexBasis={0}
                variant="outline"
                colorScheme="primary"
                borderRadius={50}
                mt="$3"
                href={
                  selectableBrief.videoUrl ||
                  'https://www.tiktok.com/@trybounty'
                }
                event="Content inspo"
              >
                <XStack mr="$2" borderColor="">
                  <BsPlayCircleFill />
                </XStack>
                See example
              </LinkButton>
              <LinkButton
                size="$sm"
                flexGrow={1}
                flexBasis={0}
                borderRadius={50}
                mt="$3"
                onPress={() => {
                  setAcceptBounty((prev) => ({
                    ...prev,
                    selectedBrief: selectableBrief,
                  }))
                }}
                href={`/bounties/${bounty.id}/accept/content-type`}
                event="Brief Card Content Type Time Clicked"
              >
                Select
              </LinkButton>
            </XStack>
          </YStack>
        )
      })}
    </YStack>
  )
}

export const BriefList = () => {
  const bounty = useBounty()

  const featuresToSend = () => {
    if (bounty.type === 'RETAIL')
      return isNotNil(bounty.store.retailBrief?.features)
        ? bounty.store.retailBrief?.features
        : bounty.bountyDefaultBrief?.features ?? DEFAULT_PRODUCT_FEATURES
    else
      return isNotNil(bounty.product?.productInstruction?.features)
        ? bounty.product?.productInstruction?.features
        : bounty.bountyDefaultBrief?.features ?? DEFAULT_PRODUCT_FEATURES
  }

  const features = constructFeaturesOrFallback(featuresToSend())

  return (
    <YStack overflow="hidden">
      {features.map((x, i) => {
        return (
          <YStack>
            <XStack
              mb="$2"
              px="$4"
              py="$2"
              borderRadius={'$3'}
              backgroundColor="$primary.100"
            >
              <Text
                data-testid={`briefListItem:${i}`}
                size="$sm"
                color="$primary.900"
              >
                {x}
              </Text>
            </XStack>
            <YStack
              key={x}
              borderRadius={'$0'}
              width="$0"
              height="$6"
              borderLeftWidth="$0"
              borderLeftColor={'transparent'}
              borderRightWidth="$5"
              borderRightColor={'transparent'}
              borderTopWidth="$5"
              borderTopColor={'$primary.100'}
              marginTop="$0"
              marginRight="$0"
              marginBottom="$0"
              marginLeft="$0"
              left="$0"
              bottom="$4"
            ></YStack>
          </YStack>
        )
      })}
    </YStack>
  )
}

export const SparkCodes = () => {
  const [open, setOpen] = useState(false)
  const { Form, Input, SubmitButton } = useFastForm({
    schema: z.object({
      code: z.string().min(10, 'Should be at least 10 characters'),
    }),
  })
  const bounty = useBounty()

  /**
   * This file is laid out weird because if you unmount the sheet before dismissing it on native it takes over the whole screen and
   * the user is stuck in a broken state. Also, it makes it to where the animations run properly.
   */
  const [acceptRequest, { loading: acceptRequestLoading }] = useMutation(
    AcceptSparkAdCodeRequestDocument,
    { fetchPolicy: 'no-cache' },
  )

  const hasNoSparkAdRequests =
    !bounty.tikTokVideo || bounty.tikTokVideo.sparkAdCodeRequests?.length === 0

  const sparkCodeRequest = (bounty.tikTokVideo?.sparkAdCodeRequests ?? [])[0]
  const amount = prettyCurrency(sparkCodeRequest?.amount)
  const storeName = bounty.store.name
  const message =
    sparkCodeRequest?.requestLog?.find((l) => l.status === 'CREATED')
      ?.message ?? ''
  const requestId = sparkCodeRequest?.id

  const SheetContents = () => {
    if (hasNoSparkAdRequests) return null
    return (
      <YStack>
        <XStack mb="$6">
          <ReceivedMessageItem
            userId="xxx"
            item={{
              __typename: 'Message',
              message: message,
              sender: {
                __typename: 'MessageSender',
                id: bounty.store.id,
                avatarUrl: bounty.store.logoImgLink,
                name: bounty.store.name,
              },
              id: 'xxx',
              isSystem: false,
              sentAt: sparkCodeRequest?.createdAt,
              type: 'SPARK_CODE_REQUEST',
            }}
          />
        </XStack>
        <Form
          onSubmit={async ({ code }) => {
            const resp = await acceptRequest({
              variables: { code, requestId },
              update(cache) {
                cache.modify({
                  id: cache.identify(bounty.tikTokVideo as StoreObject),
                  fields: {
                    sparkAdCodeRequests() {
                      return {
                        delete: [],
                      }
                    },
                  },
                })
              },
            })

            if (
              resp.data?.acceptSparkAdCodeRequest.status === 'CREATOR_ACCEPTED'
            ) {
              setOpen(false)

              toast.success({
                title: 'Spark code submitted!',
                message: `As long as the code works successfully, ${amount} will be added to your wallet in 48 hours.`,
              })
            } else {
              toast.success({
                title: 'Something went wrong.',
                message: `Please wait a few moments and try again.`,
              })
            }
          }}
        >
          <Input label="Spark Code" name="code" placeholder="Paste code here" />
          <SubmitButton
            isLoading={acceptRequestLoading}
            event="Spark Code Request Submit Code Clicked"
          >
            Submit code
          </SubmitButton>
          <LinkButton
            alignSelf="center"
            event="Spark Code Request How to Submit A Spark Code Clicked"
            variant="link"
            colorScheme="neutral"
            href="https://bnty.app/SRNixi"
          >
            How do I submit a spark code?
          </LinkButton>
        </Form>
      </YStack>
    )
  }

  return (
    <>
      {hasNoSparkAdRequests === false ? (
        <SectionGroup>
          <SectionGroupTitle>Spark Code Request</SectionGroupTitle>
          <SectionGroupDescription mb="$4">
            <Text
              data-testid="sparkCodeStoreName"
              fontWeight={'$semibold'}
              size="$sm"
            >
              {storeName}
            </Text>{' '}
            wants to use your content for a 30 day Spark Code. Unlock{' '}
            <Text
              data-testid="sparkCodeAmount"
              fontWeight={'$semibold'}
              size="$sm"
            >
              {amount}
            </Text>{' '}
            by providing the code.
          </SectionGroupDescription>
          <Button
            onPress={() => setOpen(true)}
            size="$sm"
            event="Bounty Detail Add Spark Code Clicked"
          >
            Add Spark Code
          </Button>
        </SectionGroup>
      ) : null}

      <Sheet
        // Need to be tall because brands can add a 250 character message
        snapPoints={[90]}
        open={open}
        onOpenChange={setOpen}
        title="Submit Spark Code"
      >
        <SheetContents />
      </Sheet>
    </>
  )
}

export const CommunityRulesSheet = () => {
  const [show, setShow] = useShowCommunityRules()
  const { data, isLoading } = useSWRSanity(
    'getGenericDetailPage',
    'content-guidelines',
  )

  return (
    <Sheet
      scroll
      // Need to be tall because brands can add a 250 character message
      snapPoints={[90]}
      open={show}
      onOpenChange={setShow}
    >
      {isLoading ? (
        <PageLoading />
      ) : (
        <PagePortableText item={data} pageProps={{ framed: false }} />
      )}
    </Sheet>
  )
}

type ContentOptionType = { name: string; value: string }

export const ContentOption = ({ name, value }: ContentOptionType) => {
  const bounty = useBounty()
  const [, setAcceptBounty] = useAcceptBounty()

  return (
    <LinkButton
      href={`/bounties/${bounty.id}/accept/brief`}
      onPress={() => {
        setAcceptBounty((prev) => ({
          ...prev,
          bountyContentType: value as UserBountyContentType,
        }))
      }}
      variant="solid"
      backgroundColor="white"
      colorScheme="whiteAlpha"
      flex={1}
      height="100%"
      borderRadius="$3"
      borderWidth={1}
      borderColor="$neutral.200"
      justifyContent="flex-start"
      alignItems="center"
      event={{
        eventName: 'Bounty Accept Content Type Next Clicked',
        contentType: value,
        bountyId: bounty.id,
        shopUrl: bounty.store.shopUrl,
        shopName: bounty.store.name,
        productId: bounty.product?.id,
        productName: bounty.product?.productName,
        hasProductBrief: !!bounty.product?.productInstruction,
      }}
    >
      <XStack
        alignItems="center"
        justifyContent="center"
        space="$2"
        py="$3"
        flexGrow={1}
        flexShrink={1}
      >
        <Image
          src={value === 'TIKTOK_VIDEO' ? TikTokLogo : InstagramLogo}
          height={40}
          width={40}
          resizeMode="contain"
        />
        <Text fontWeight={'$semibold'} size="$lg" color="$neutral.900">
          {name}
        </Text>
      </XStack>
    </LinkButton>
  )
}

export const ContentTypeForm = () => {
  return (
    <YStack backgroundColor="$neutral.200" mb="$6" gap="$6">
      <H1 size="$2xl" color="$primary.800">
        Where do you want to post first?
      </H1>
      <YStack gap="$3">
        {BOUNTY_CONTENT_TYPES.map((contentType) => (
          <ContentOption
            name={contentType.name}
            value={contentType.value}
            key={contentType.value}
          />
        ))}
      </YStack>
      <Text color={'$neutral.600'} mb="$6">
        Don’t worry, you can post to another platform later
      </Text>
    </YStack>
  )
}

export type BriefDetailsCardProps = {
  name: string
  content: string
  minimumPayoutAmount: number
}

export const BriefDetailsCard = ({
  name,
  content,
  minimumPayoutAmount,
}: BriefDetailsCardProps) => {
  const { convertHyperlink } = useHyperlinkParser()
  return (
    <YStack
      backgroundColor={'$primary.100'}
      borderRadius={'$6'}
      px="$5"
      py="$4"
      mb="$4"
      gap="$3"
    >
      <XStack gap="$3" alignItems="center" justifyContent="space-between">
        <Text size="$lg" numberOfLines={1} fontFamily="$heading">
          {name}
        </Text>
        <Tag
          colorScheme="blackAlpha"
          backgroundColor="white"
          textProps={{ fontFamily: '$heading', py: '$1' }}
        >
          ${minimumPayoutAmount} Min
        </Tag>
      </XStack>
      <Text size="$sm">{convertHyperlink(content)}</Text>
    </YStack>
  )
}

export const BriefsCard = () => {
  const bounty = useBounty()
  const [{ bountyContentType, selectedBrief }] = useAcceptBounty()

  return (
    <YStack>
      {bounty.briefId ? (
        <BriefDetailsCard
          name={bounty.brief?.name || ''}
          content={bounty.brief?.content || ''}
          minimumPayoutAmount={
            bounty.brief?.paymentSnapshot?.minBountyPayment ?? 0
          }
        />
      ) : selectedBrief ? (
        <BriefDetailsCard
          name={selectedBrief.name}
          content={selectedBrief.content}
          minimumPayoutAmount={
            selectedBrief.paymentSnapshot?.minBountyPayment ?? 0
          }
        />
      ) : (
        <BriefList />
      )}
      <a
        style={{
          flex: 1,
          display: 'flex',
          textDecoration: 'none',
          flexBasis: 'max-content',
        }}
        target="_blank"
        rel="noreferrer"
        href={bounty.brief?.videoUrl || 'https://www.tiktok.com/@trybounty'}
      >
        <Button
          flex={1}
          mb="$4"
          size="$sm"
          borderRadius={50}
          variant="outline"
          colorScheme="primary"
          event="Clicked Content Inspo"
        >
          <XStack mr="$2" borderColor="">
            <BsPlayCircleFill />
          </XStack>
          See example
        </Button>
      </a>
      {bountyContentType === 'TIKTOK_VIDEO' && <RulePostingInstructions />}
      {bountyContentType === 'INSTAGRAM_REEL' && <RuleTag />}
      <RuleHashtag />
    </YStack>
  )
}
